import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Bell, Check, CheckCheck, Trash2, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api';

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const ref = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();

  const updateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
  };

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClick = (e) => {
      // Check if clicking outside both the button and the dropdown
      if (
        buttonRef.current && !buttonRef.current.contains(e.target) &&
        ref.current && !ref.current.contains(e.target)
      ) {
        // Don't close if clicking on action buttons
        const isActionButton = e.target.closest('[data-action-button="true"]');
        if (!isActionButton) {
          setOpen(false);
        }
      }
    };
    
    if (open) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [open]);

  const markRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      await fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Still try to refresh in case it actually worked
      fetchNotifications();
    }
  };

  const markAllRead = async () => {
    if (loading || unreadCount === 0) return;
    
    setLoading(true);
    try {
      const response = await api.put('/notifications/read-all');
      console.log('✅ Mark all read response:', response.data);
      await fetchNotifications();
      console.log('✅ Notifications refreshed');
    } catch (error) {
      console.error('❌ Error marking all notifications as read:', error);
      console.error('Error details:', error.response?.data);
      // Still try to refresh
      await fetchNotifications();
    } finally {
      setLoading(false);
    }
  };

  const deleteNotification = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      await fetchNotifications();
    } catch (error) {
      console.error('Error deleting notification:', error);
      fetchNotifications();
    }
  };

  const handleClick = (n) => {
    // Mark as read if unread
    if (!n.read) markRead(n.id);
    
    // Determine navigation path based on notification type and metadata
    let navigationPath = '/approvals'; // Default fallback
    
    // Check if notification has a direct link
    if (n.link) {
      navigationPath = n.link;
    } 
    // Check metadata for related_id (collection/proposal ID)
    else if (n.metadata?.related_id) {
      const relatedId = n.metadata.related_id;
      
      // Determine path based on notification type
      if (n.type?.includes('submission') || n.type?.includes('pending_approval')) {
        // Navigate to approvals page
        navigationPath = '/approvals';
      } 
      else if (n.type?.includes('approved') || n.type?.includes('rejected') || n.type?.includes('reopened')) {
        // Navigate to specific proposal view
        navigationPath = `/proposals/${relatedId}`;
      }
      else if (n.type?.includes('comment') || n.type?.includes('feedback')) {
        // Navigate to proposal view to see comments
        navigationPath = `/proposals/${relatedId}`;
      }
      else if (n.type?.includes('threshold') || n.type?.includes('settings')) {
        // Navigate to settings
        navigationPath = '/settings';
      }
      else {
        // Default to approvals for any approval-related notification
        navigationPath = '/approvals';
      }
    }
    // Check notification category for routing hints
    else if (n.category === 'approval' || n.category === 'escalation') {
      navigationPath = '/approvals';
    }
    else if (n.category === 'info' && n.message?.includes('approved')) {
      // If it's an info notification about approval, go to proposals
      navigationPath = '/proposals';
    }
    
    // Navigate and close dropdown
    navigate(navigationPath);
    setOpen(false);
  };

  const categoryColors = {
    approval: 'bg-sky-500',
    escalation: 'bg-amber-500',
    info: 'bg-slate-400',
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        data-testid="notification-bell"
        onClick={() => {
          updateDropdownPosition();
          setOpen(!open);
        }}
        className="relative p-2 text-slate-400 hover:text-slate-200 hover:bg-white/[0.06] rounded-lg transition-colors"
      >
        <Bell className="w-[18px] h-[18px]" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && createPortal(
        <div 
          ref={ref}
          className="fixed w-80 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
          style={{ 
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
            maxHeight: '420px',
            zIndex: 9999
          }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-slate-800">Notifications</h3>
                <p className="text-[10px] text-slate-400 mt-0.5">Click any notification to view details</p>
              </div>
              {unreadCount > 0 && (
                <button
                  data-testid="mark-all-read"
                  data-action-button="true"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    markAllRead();
                  }}
                  disabled={loading}
                  className={`text-xs font-medium flex items-center gap-1 transition-colors ${
                    loading 
                      ? 'text-slate-400 cursor-not-allowed' 
                      : 'text-sky-600 hover:text-sky-700 cursor-pointer'
                  }`}
                >
                  <CheckCheck className="w-3.5 h-3.5" /> 
                  {loading ? 'Marking...' : 'Mark all read'}
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="overflow-y-auto" style={{ maxHeight: '340px' }}>
            {notifications.length === 0 ? (
              <div className="py-12 px-6 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-3">
                  <Bell className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-sm font-medium text-slate-600 mb-1">All caught up!</p>
                <p className="text-xs text-slate-400">No new notifications</p>
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-all duration-150 hover:shadow-sm group ${
                    !n.read ? 'bg-sky-50/40' : ''
                  }`}
                  onClick={() => handleClick(n)}
                  title="Click to view details"
                >
                  <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                    !n.read ? (categoryColors[n.category || n.type?.split('_')[1]] || 'bg-sky-500') : 'bg-transparent'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-1.5">
                      <p className={`text-xs leading-snug flex-1 ${!n.read ? 'font-semibold text-slate-800' : 'text-slate-600'}`}>
                        {n.title}
                      </p>
                      <ExternalLink className="w-3 h-3 text-slate-300 group-hover:text-sky-500 transition-colors flex-shrink-0 mt-0.5" />
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{n.message}</p>
                    <p className="text-[10px] text-slate-300 mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!n.read && (
                      <button
                        data-action-button="true"
                        onClick={(e) => { 
                          e.preventDefault();
                          e.stopPropagation(); 
                          markRead(n.id); 
                        }}
                        className="p-1 text-slate-300 hover:text-sky-500 transition-colors cursor-pointer"
                        title="Mark as read"
                        data-testid={`mark-read-${n.id}`}
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      data-action-button="true"
                      onClick={(e) => { 
                        e.preventDefault();
                        e.stopPropagation(); 
                        deleteNotification(n.id); 
                      }}
                      className="p-1 text-slate-300 hover:text-red-400 transition-colors cursor-pointer"
                      title="Delete"
                      data-testid={`delete-${n.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
